import React from 'react';
import { Link } from '@tanstack/react-router';
import { FileText, User, FolderOpen, Calendar, Eye } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SearchResult } from '../services/search-service';
import { transformR2PathToPublicUrl } from '@/utils/r2-url-transformer';

interface SearchResultItemProps {
  result: SearchResult;
}

const SearchResultItem: React.FC<SearchResultItemProps> = ({ result }) => {
  const getIcon = () => {
    switch (result.type) {
      case 'document':
        return <FileText className="h-6 w-6 text-blue-500" />;
      case 'profile':
        return <User className="h-6 w-6 text-green-500" />;
      case 'collection':
        return <FolderOpen className="h-6 w-6 text-amber-500" />;
      default:
        return <FileText className="h-6 w-6 text-gray-500" />;
    }
  };

  const getTypeBadge = () => {
    const colors = {
      document: 'bg-blue-100 text-blue-800',
      profile: 'bg-green-100 text-green-800',
      collection: 'bg-amber-100 text-amber-800',
    };

    return (
      <Badge variant="secondary" className={`capitalize ${colors[result.type]}`}>
        {result.type}
      </Badge>
    );
  };

  const getNavigationPath = () => {
    switch (result.type) {
      case 'document':
        return `/documents/${result.id}`;
      case 'profile':
        return `/profiles/${result.id}`;
      case 'collection':
        return `/collections/${result.id}`;
      default:
        return '#';
    }
  };

  const getMetadataDisplay = () => {
    const { metadata } = result;
    const items = [];

    if (metadata.documentType) {
      items.push(
        <span key="docType" className="flex items-center gap-1 text-xs text-muted-foreground">
          <FileText className="h-3 w-3" />
          {metadata.documentType}
        </span>
      );
    }

    if (metadata.originalDate) {
      items.push(
        <span key="date" className="flex items-center gap-1 text-xs text-muted-foreground">
          <Calendar className="h-3 w-3" />
          {metadata.originalDate}
        </span>
      );
    }

    if (metadata.positionType) {
      items.push(
        <span key="position" className="flex items-center gap-1 text-xs text-muted-foreground">
          <User className="h-3 w-3" />
          {metadata.positionType.replace('_', ' ')}
        </span>
      );
    }

    if (metadata.documentCount !== undefined) {
      items.push(
        <span key="count" className="flex items-center gap-1 text-xs text-muted-foreground">
          <FolderOpen className="h-3 w-3" />
          {metadata.documentCount} documents
        </span>
      );
    }

    if (metadata.visibility) {
      items.push(
        <span key="visibility" className="flex items-center gap-1 text-xs text-muted-foreground">
          <Eye className="h-3 w-3" />
          {metadata.visibility}
        </span>
      );
    }

    return items;
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-0">
        <Link to={getNavigationPath()} className="block p-4 hover:bg-muted/50 transition-colors">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="flex-shrink-0 mt-1">
              {getIcon()}
            </div>

            {/* Thumbnail */}
            {result.thumbnail && (
              <div className="flex-shrink-0">
                <img
                  src={transformR2PathToPublicUrl(result.thumbnail) || result.thumbnail}
                  alt={result.title}
                  className="h-16 w-16 rounded-md object-cover"
                  onError={(e) => {
                    // Hide image if it fails to load
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-semibold text-lg leading-tight truncate">
                  {result.title}
                </h3>
                {getTypeBadge()}
              </div>

              {result.description && (
                <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                  {result.description}
                </p>
              )}

              {result.highlight && (
                <p className="text-sm text-muted-foreground italic mb-2 bg-yellow-50 px-2 py-1 rounded">
                  {result.highlight}
                </p>
              )}

              {/* Metadata */}
              <div className="flex flex-wrap gap-3">
                {getMetadataDisplay()}
              </div>

              {/* Creator info for documents */}
              {result.metadata.creator && (
                <div className="mt-2">
                  <span className="text-xs text-muted-foreground">
                    by {result.metadata.creator}
                  </span>
                </div>
              )}
            </div>

            {/* Relevance score (for debugging - can be hidden in production) */}
            {process.env.NODE_ENV === 'development' && (
              <div className="flex-shrink-0 text-xs text-muted-foreground">
                {Math.round(result.relevanceScore * 100)}%
              </div>
            )}
          </div>
        </Link>
      </CardContent>
    </Card>
  );
};

export default SearchResultItem;
